import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubscriptionSuccessPage = () => {
  const { t } = useLanguage();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [status, setStatus] = useState('verifying');
  const [subscriptionData, setSubscriptionData] = useState(null);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      verifySubscription(sessionId);
    } else {
      setStatus('error');
    }
  }, [token, searchParams, navigate]);

  const verifySubscription = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/subscription/verify/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.status === 'success') {
        setStatus('success');
        setSubscriptionData(response.data);
      } else {
        setStatus('pending');
      }
    } catch (error) {
      console.error('Subscription verification error:', error);
      setStatus('error');
    }
  };

  const planLabels = {
    annual: 'Annuale (1 anno)',
    biennial: 'Biennale (2 anni)',
    triennial: 'Triennale (3 anni)'
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4" data-testid="subscription-success-page">
      <div className="max-w-md w-full">
        {status === 'verifying' && (
          <Card>
            <CardContent className="p-8 text-center">
              <Loader2 className="h-12 w-12 animate-spin text-orange-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-slate-900 mb-2">Verifica in corso...</h2>
              <p className="text-slate-600">Stiamo verificando il tuo pagamento.</p>
            </CardContent>
          </Card>
        )}

        {status === 'success' && (
          <Card>
            <CardContent className="p-8 text-center">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Abbonamento Attivato!</h2>
              <p className="text-slate-600 mb-4">
                Il tuo abbonamento è stato rinnovato con successo.
              </p>
              
              {subscriptionData && (
                <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6 text-left">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Piano:</span>
                      <span className="font-medium text-slate-900">
                        {planLabels[subscriptionData.plan] || subscriptionData.plan}
                      </span>
                    </div>
                    {subscriptionData.expires_at && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Scadenza:</span>
                        <span className="font-medium text-slate-900">
                          {new Date(subscriptionData.expires_at).toLocaleDateString('it-IT')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <Button 
                className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                onClick={() => navigate('/dashboard')}
                data-testid="go-to-dashboard-btn"
              >
                Vai alla Dashboard
              </Button>
            </CardContent>
          </Card>
        )}

        {status === 'pending' && (
          <Card>
            <CardContent className="p-8 text-center">
              <Loader2 className="h-12 w-12 text-orange-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-slate-900 mb-2">Pagamento in elaborazione</h2>
              <p className="text-slate-600 mb-4">
                Il pagamento è ancora in elaborazione. Riprova tra qualche minuto.
              </p>
              <Button 
                variant="outline"
                onClick={() => window.location.reload()}
                data-testid="retry-verification-btn"
              >
                Riprova
              </Button>
            </CardContent>
          </Card>
        )}

        {status === 'error' && (
          <Card>
            <CardContent className="p-8 text-center">
              <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-slate-900 mb-2">Errore di verifica</h2>
              <p className="text-slate-600 mb-4">
                Non è stato possibile verificare il pagamento. Se hai completato il pagamento, contattaci.
              </p>
              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  className="flex-1"
                  onClick={() => navigate('/subscription')}
                  data-testid="back-to-subscription-btn"
                >
                  Torna agli abbonamenti
                </Button>
                <Button 
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                  onClick={() => navigate('/dashboard')}
                  data-testid="go-to-dashboard-error-btn"
                >
                  Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage;
